import React, { useEffect, useState } from 'react';
import { AfterSubmitFunc, FormTemplate, Text, TextLink } from 'ui-builder';
import { SenderEmail, SetVerificationCodeRequest, useSenderEmailService } from 'nekst-api';
import { VerificationCodeInput } from 'features/nekst-widgets';

import styles from './SenderEmail.module.scss';


function ResendLink(
  props: {
    personId: number,
    onResend: (senderEmail: SenderEmail) => void,
  }
) {
  const senderEmailService = useSenderEmailService();

  const [loading, setLoading] = useState(false);

  if (loading) {
    return <Text className={styles.hint}>Resending...</Text>;
  } else {
    return (
      <TextLink
        onClick={async () => {
          setLoading(true);
          const result = await senderEmailService.resendCode(props.personId);
          setLoading(false);
          props.onResend(result);
        }}
        className={styles.hint}
      >
        Resend Verification Code
      </TextLink>
    );
  }
}


function ResendCodeWidget(
  props: {
    senderEmail: SenderEmail,
    personId: number,
  }
) {
  const [resendInSeconds, setResendInSeconds] = useState(
    props.senderEmail.verificationCodeCanBeSentAt ?
      Math.round((props.senderEmail.verificationCodeCanBeSentAt.getTime() - new Date().getTime()) / 1000)
      : 0
  );

  useEffect(() => {
    if (resendInSeconds) {
      const interval = setInterval(
        () => setResendInSeconds(prev => prev! - 1),
        1005
      );
      return () => clearInterval(interval);
    } else {
      return () => null;
    }
  }, [resendInSeconds]);

  if (resendInSeconds <= 0) {
    return (
      <ResendLink
        personId={props.personId}
        onResend={(email) => {
          setResendInSeconds(
            email.verificationCodeCanBeSentAt ?
              Math.round((email.verificationCodeCanBeSentAt.getTime() - new Date().getTime()) / 1000)
              : 0
          );
        }}
      />
    );
  } else {
    return <Text className={styles.hint}>You can resend the code in {resendInSeconds} seconds.</Text>;
  }
}


export default function VerificationCodeForm(
  props: {
    senderEmail: SenderEmail,
    personId: number,
    afterSubmitFunc?: AfterSubmitFunc,
  }
) {
  const senderEmailService = useSenderEmailService();

  return (
    <FormTemplate<SetVerificationCodeRequest, SenderEmail>
      title="Verify Your Email"
      submitFormFunc={async (data) => {
        return await senderEmailService.verifyCode(props.personId, data);
      }}
      afterSubmitFunc={props.afterSubmitFunc}
    >
      <Text>Please enter the verification code we just sent to email <Text bold>{props.senderEmail.email}</Text>.</Text>
      <VerificationCodeInput source="verificationCode" />
      <ResendCodeWidget personId={props.personId} senderEmail={props.senderEmail} />
    </FormTemplate>
  );
}
